import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const stays = [
  {
    name: "Ridge Suite",
    idx: "01",
    price: "From CHF 1,450 / night",
    detail: "2 Guests — 68 m² — Mountain View",
    copy: "Floor-to-ceiling glass and quiet spaces suspended above the valley.",
    image:
      "https://readdy.ai/api/search-image?query=Contemporary%20alpine%20luxury%20suite%20with%20expansive%20floor%20to%20ceiling%20glass%20framing%20bright%20snow%20covered%20mountain%20peaks%2C%20cool%20natural%20daylight%2C%20refined%20stone%20and%20timber%20materials%2C%20low%20profile%20linen%20bed%2C%20minimalist%20international%20hospitality%20aesthetic%2C%20editorial%20architectural%20photography%2C%20quiet%20luxury%2C%20mineral%20neutral%20palette%20with%20cold%20daylight%2C%20fine%20art%20composition%20with%20negative%20space&width=2400&height=1600&seq=stay-ridge-v2-01&orientation=landscape&nocache=true",
  },
  {
    name: "Forest Cabin",
    idx: "02",
    price: "From CHF 1,180 / night",
    detail: "2 Guests — 52 m² — Private Cabin",
    copy: "A secluded timber retreat wrapped in pine forest, firelight, and alpine stillness.",
    image:
      "https://readdy.ai/api/search-image?query=Secluded%20contemporary%20alpine%20timber%20cabin%20interior%20surrounded%20by%20dense%20snowy%20pine%20forest%20visible%20through%20window%2C%20warm%20firelight%20glow%20from%20stone%20fireplace%2C%20intimate%20atmosphere%2C%20natural%20oak%20and%20wool%20materials%2C%20understated%20quiet%20luxury%20hospitality%2C%20editorial%20architectural%20photography%2C%20muted%20warm%20interior%20palette%20with%20cool%20forest%20light%20outside%2C%20fine%20art%20composition&width=2400&height=1600&seq=stay-forest-v2-01&orientation=landscape&nocache=true",
  },
  {
    name: "Summit Residence",
    idx: "03",
    price: "From CHF 2,760 / night",
    detail: "4 Guests — 124 m² — Panoramic View",
    copy: "An expansive private residence with uninterrupted views across the ridgelines.",
    image:
      "https://readdy.ai/api/search-image?query=Expansive%20contemporary%20alpine%20private%20residence%20with%20dramatic%20panoramic%20glazing%20framing%20highest%20snowy%20ridgelines%20at%20dusk%2C%20refined%20stone%20and%20timber%20architecture%2C%20generous%20open%20living%20space%2C%20subtle%20warm%20dusk%20lighting%2C%20editorial%20luxury%20hospitality%20photography%2C%20quiet%20luxury%2C%20muted%20mineral%20palette%20with%20soft%20dusk%20sky%2C%20fine%20art%20composition%20with%20negative%20space&width=2400&height=1600&seq=stay-summit-v2-01&orientation=landscape&nocache=true",
  },
];

export default function Stay() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const panels = gsap.utils.toArray<HTMLElement>(".stay-panel");
      panels.forEach((panel, i) => {
        if (i === 0) return;
        gsap.fromTo(
          panel,
          { clipPath: "inset(100% 0% 0% 0%)" },
          {
            clipPath: "inset(0% 0% 0% 0%)",
            ease: "none",
            scrollTrigger: {
              trigger: ref.current,
              start: () => `top+=${(i - 0.5) * window.innerHeight} top`,
              end: () => `top+=${(i + 0.5) * window.innerHeight} top`,
              scrub: true,
            },
          }
        );
      });

      ScrollTrigger.create({
        trigger: ref.current,
        start: "top top",
        end: () => `+=${stays.length * window.innerHeight}`,
        pin: ".stay-pin",
        pinSpacing: true,
      });

      const indicators = gsap.utils.toArray<HTMLElement>(".stay-indicator");
      panels.forEach((_, i) => {
        ScrollTrigger.create({
          trigger: ref.current,
          start: () => `top+=${i * window.innerHeight} top`,
          end: () => `top+=${(i + 1) * window.innerHeight} top`,
          onToggle: (self) => {
            if (self.isActive) {
              indicators.forEach((el, j) => {
                el.classList.toggle("is-active", j === i);
              });
            }
          },
        });
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="stay"
      ref={ref}
      className="relative bg-background-950 text-foreground-50"
      style={{ height: `${(stays.length + 1) * 100}vh` }}
    >
      <div className="stay-pin relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          {stays.map((s, i) => (
            <div
              key={s.name}
              className="stay-panel absolute inset-0 will-change-[clip-path]"
              style={{ zIndex: i + 1 }}
            >
              <img
                src={s.image}
                alt={s.name}
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/25 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />

              <div className="absolute inset-0 z-10 px-6 md:px-10 pt-28 pb-14 flex flex-col justify-between">
                <div className="flex items-center gap-4">
                  <span className="w-8 h-px bg-foreground-50/40" />
                  <span className="eyebrow text-foreground-50/70">04 — Stay</span>
                </div>

                <div className="grid md:grid-cols-12 gap-10 items-end">
                  <div className="md:col-span-7">
                    <div className="eyebrow text-foreground-50/60 mb-4">
                      {s.idx} / 0{stays.length}
                    </div>
                    <h3 className="font-display font-light text-4xl md:text-[52px] leading-[1.02] tracking-tight text-foreground-50">
                      {s.name}
                    </h3>
                    <p className="mt-5 max-w-xl text-foreground-50/85 text-[15px] md:text-base font-light leading-relaxed">
                      {s.copy}
                    </p>
                  </div>

                  <div className="md:col-span-5 md:pl-10 md:border-l border-foreground-50/20 space-y-6">
                    <div>
                      <div className="eyebrow text-foreground-50/60">Detail</div>
                      <p className="font-display text-base md:text-lg text-foreground-50 mt-2">
                        {s.detail}
                      </p>
                    </div>
                    <div>
                      <div className="eyebrow text-foreground-50/60">Rate</div>
                      <p className="font-display text-base md:text-lg text-foreground-50 mt-2">
                        {s.price}
                      </p>
                    </div>
                    <a
                      href="#book"
                      className="inline-block eyebrow whitespace-nowrap px-6 py-4 border border-foreground-50/50 text-foreground-50 hover:bg-foreground-50 hover:text-background-950 transition-colors cursor-pointer"
                    >
                      Reserve {s.name}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute top-1/2 right-6 md:right-10 -translate-y-1/2 z-20 flex flex-col gap-4">
          {stays.map((s, i) => (
            <div
              key={s.name}
              className={`stay-indicator flex items-center gap-3 text-foreground-50/50 ${
                i === 0 ? "is-active" : ""
              }`}
            >
              <span className="eyebrow tabular-nums">0{i + 1}</span>
              <span className="h-px w-8 bg-foreground-50/30 indicator-bar transition-all duration-500" />
            </div>
          ))}
        </div>

        <style>{`
          .stay-indicator.is-active { color: oklch(var(--foreground-50)); }
          .stay-indicator.is-active .indicator-bar { background: oklch(var(--foreground-50)); width: 3rem; }
        `}</style>
      </div>
    </section>
  );
}